import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Lock, Eye, EyeOff, ArrowRight, AlertCircle, CheckCircle } from "lucide-react";
import applogo from "../../assets/Images/applogo.png";
import { useDispatch } from "react-redux";
import { loginWithPasswordThunk, loginWithOtp, verifyLoginOtp } from "../../redux/thunk";
import { handleApiError } from "../../utils/useSession";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";

// Reception signs in through this same screen. The backend returns role:"reception" and a
// token that can never satisfy a doctor guard, so the only thing decided here is where to
// land — a receptionist has just the Requests page, not the dashboard.
//
// A CuoreCare-enabled doctor lands on CuoreCare/Today instead of the Dashboard (the doc's
// "CuoreCare becomes the landing page"); a doctor without the flag (walk-in-only, or not yet
// configured) still lands on the Dashboard — CuoreCare's own page would just show a "not
// enabled, contact your admin" message for them, a worse first screen than the Dashboard.
// `isCuoreCareDoctor` rides along on the login response for exactly this decision, so this
// doesn't need a second round-trip to fetch the full profile before navigating.
function landingFor(userData) {
  const role = userData?.role || userData?.data?.role;
  const doctor = userData?.doctor || userData?.data?.doctor;
  localStorage.setItem("role", role === "reception" ? "reception" : "doctor");
  // Cached alongside `role` so an already-logged-in visitor landing back on /login (see the
  // effect below) can make the same decision without waiting on a profile fetch.
  localStorage.setItem("isCuoreCareDoctor", doctor?.isCuoreCareDoctor ? "1" : "0");
  if (role === "reception") return "/requests";
  return doctor?.isCuoreCareDoctor ? "/cuorecare" : "/home";
}

export default function CuoreLoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [mobileInput, setMobileInput] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [otpSent, setOtpSent] = useState(false);
  const [password, setPassword] = useState("");
  const [loginMode, setLoginMode] = useState("otp");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [notice, setNotice] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const otpRefs = useRef([]);

  useEffect(() => {
    if (loginMode !== "otp") { setNotice(""); setOtpSent(false); }
  }, [loginMode]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    if (localStorage.getItem("role") === "reception") {
      navigate("/requests", { replace: true });
    } else {
      navigate(localStorage.getItem("isCuoreCareDoctor") === "1" ? "/cuorecare" : "/home", { replace: true });
    }
  }, [navigate]);

  const handleOtpChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);
    if (value && index < 3) otpRefs.current[index + 1]?.focus();
    if (!value && index > 0) otpRefs.current[index - 1]?.focus();
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const validateMobile = () => {
    if (!/^\d{10}$/.test(mobileInput)) {
      setErrors({ mobile: "Enter a valid 10-digit mobile number." });
      return false;
    }
    return true;
  };

  const handleSendOtp = async () => {
    if (!validateMobile()) return;
    setLoading(true); setErrors({}); setNotice("");
    try {
      const result = await dispatch(loginWithOtp({ mobileNumber: `+91${mobileInput}` }));
      if (result.type?.endsWith("/rejected")) {
        const msg = result.payload?.message || result.payload?.error || "No doctor found with this number.";
        setErrors({ otp: msg });
      } else if (result?.payload?.success || result?.payload?.data?.success) {
        const testOtp = result?.payload?.data?.test_otp || result?.payload?.test_otp;
        setNotice(`OTP sent!${testOtp ? ` Test OTP: ${testOtp}` : ""}`);
        setOtpSent(true);
      } else {
        setErrors({ otp: handleApiError(result?.error || result?.payload, null) || "Failed to send OTP." });
      }
    } catch (err) {
      setErrors({ otp: handleApiError(err, null) || "Failed to send OTP." });
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async () => {
    if (otp.join("").length !== 4) { setErrors({ otp: "Enter a valid 4-digit OTP." }); return; }
    setLoading(true); setErrors({}); setNotice("");
    try {
      const result = await dispatch(verifyLoginOtp({ mobileNumber: `+91${mobileInput}`, otp: otp.join("") }));
      if (result.type?.endsWith("/rejected")) {
        const msg = result.payload?.message || result.payload?.error || "Invalid OTP. Please try again.";
        setErrors({ otp: msg }); setOtp(["", "", "", ""]); otpRefs.current[0]?.focus();
      } else if (result?.payload?.success || result?.payload?.data?.success) {
        const userData = result?.payload?.data || result?.payload;
        if (userData) localStorage.setItem("userData", JSON.stringify(userData));
        navigate(landingFor(userData), { replace: true });
      } else {
        const msg = handleApiError(result?.error || result?.payload, null) || "Invalid OTP.";
        setErrors({ otp: msg }); setOtp(["", "", "", ""]); otpRefs.current[0]?.focus();
      }
    } catch (err) {
      setErrors({ otp: handleApiError(err, null) || "Invalid OTP." });
      setOtp(["", "", "", ""]); otpRefs.current[0]?.focus();
    } finally { setLoading(false); }
  };

  const handlePasswordLogin = async () => {
    if (!validateMobile()) return;
    if (password.length < 6) { setErrors({ password: "Password must be at least 6 characters." }); return; }
    setLoading(true); setErrors({});
    try {
      const result = await dispatch(loginWithPasswordThunk({ mobileNumber: `+91${mobileInput}`, password }));
      if (result.type?.endsWith("/rejected")) {
        const msg = result.payload?.message || result.payload?.error || "Invalid credentials.";
        setErrors({ password: msg }); setPassword("");
      } else if (result?.payload?.success || result?.payload?.data?.success) {
        const userData = result?.payload?.data || result?.payload;
        if (userData) localStorage.setItem("userData", JSON.stringify(userData));
        setSuccessMsg("Login successful! Redirecting…");
        setTimeout(() => navigate(landingFor(userData), { replace: true }), 900);
      } else {
        const msg = handleApiError(result?.error || result?.payload, null) || "Login failed.";
        setErrors({ password: msg }); setPassword("");
      }
    } catch (err) {
      setErrors({ password: handleApiError(err, null) || "Login failed." });
      setPassword("");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">

      {/* Left panel — branding */}
      <div className="hidden lg:flex flex-col w-1/2 bg-gradient-to-br from-brand-600 via-brand-700 to-brand-900 p-12 relative overflow-hidden">
        {/* Background circles */}
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute -bottom-32 -right-24 w-80 h-80 rounded-full bg-white/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-white/5" />

        {/* Logo — pinned top */}
        <div className="relative shrink-0">
          <div className="mb-2">
            <div className="bg-white rounded-xl px-3 py-1.5 inline-flex">
              <img src={applogo} alt="CuoreMD" className="h-8 w-auto" />
            </div>
          </div>
          <p className="text-blue-200 text-sm">Doctor Portal</p>
        </div>

        {/* Heading + features — vertically centered */}
        <div className="relative flex-1 flex flex-col justify-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }}>
            <h2 className="text-4xl font-bold text-white leading-tight mb-3">
              Welcome to CuoreMD™
            </h2>
            <p className="text-blue-200 text-base leading-relaxed max-w-sm mb-10">
              Smarter insights. Stronger decisions. Better outcomes.
            </p>
          </motion.div>

          <div className="space-y-5">
            {[
              { title: "Track Risk",      desc: "Monitor cardiometabolic risk in real time." },
              { title: "Boost Follow-up", desc: "Strengthen continuity and patient engagement." },
              { title: "Red Flag Alerts", desc: "Act early with timely risk notifications." },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + i * 0.12, duration: 0.4 }}
                className="flex items-start gap-4"
              >
                <div className="w-2 h-2 rounded-full bg-blue-300 shrink-0 mt-2" />
                <div>
                  <p className="text-sm font-semibold text-white">{item.title}</p>
                  <p className="text-xs text-blue-200 mt-0.5 leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Footer — pinned bottom */}
        <p className="relative shrink-0 text-xs text-blue-300">CuoreMD™ — Supporting You Every Step of the Way.</p>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <div className="mb-8 lg:hidden">
            <img src={applogo} alt="CuoreMD" className="h-7 w-auto" />
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">Welcome back</h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm">Sign in to your doctor account</p>
          </div>

          {/* Mode tabs */}
          <div className="flex bg-slate-100 dark:bg-slate-800 rounded-xl p-1 mb-6 gap-1">
            {[{ id: "otp", label: "OTP Login" }, { id: "password", label: "Password" }].map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setLoginMode(tab.id); setErrors({}); setOtpSent(false); setOtp(["","","",""]); }}
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  loginMode === tab.id
                    ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                    : "text-slate-500 dark:text-slate-400 hover:text-slate-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Success */}
          <AnimatePresence>
            {successMsg && (
              <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl mb-4 text-sm text-green-700 dark:text-green-300">
                <CheckCircle size={16} /> {successMsg}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="space-y-4">
            {/* Mobile number */}
            <Input
              label="Mobile Number"
              placeholder="10-digit mobile number"
              value={mobileInput}
              onChange={(e) => { setMobileInput(e.target.value.replace(/\D/g, "").slice(0, 10)); setErrors({}); }}
              icon={<Phone size={15} />}
              error={errors.mobile}
              disabled={loginMode === "otp" && otpSent}
              maxLength={10}
            />

            {/* OTP mode */}
            <AnimatePresence mode="wait">
              {loginMode === "otp" && (
                <motion.div
                  key="otp-section"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  {notice && (
                    <p className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded-lg mb-3">
                      <CheckCircle size={13} /> {notice}
                    </p>
                  )}
                  {errors.otp && (
                    <p className="flex items-center gap-1.5 text-xs text-red-500 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg mb-3">
                      <AlertCircle size={13} /> {errors.otp}
                    </p>
                  )}

                  {otpSent && (
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Enter OTP</label>
                      <div className="flex gap-3 justify-center">
                        {otp.map((digit, i) => (
                          <input
                            key={i}
                            ref={(el) => (otpRefs.current[i] = el)}
                            value={digit}
                            onChange={(e) => handleOtpChange(e.target.value, i)}
                            onKeyDown={(e) => handleOtpKeyDown(e, i)}
                            maxLength={1}
                            className="otp-input dark:bg-slate-800 dark:text-white"
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    fullWidth
                    className="mt-4"
                    loading={loading}
                    onClick={otpSent ? handleVerifyOtp : handleSendOtp}
                    icon={!loading && <ArrowRight size={16} />}
                  >
                    {otpSent ? "Verify OTP" : "Send OTP"}
                  </Button>

                  {otpSent && (
                    <button
                      onClick={() => { setOtpSent(false); setOtp(["","","",""]); setErrors({}); setNotice(""); }}
                      className="w-full text-xs text-slate-500 hover:text-brand-600 mt-2 py-1 transition-colors"
                    >
                      Change number
                    </button>
                  )}
                </motion.div>
              )}

              {/* Password mode */}
              {loginMode === "password" && (
                <motion.div
                  key="password-section"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-4"
                >
                  <Input
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setErrors({}); }}
                    icon={<Lock size={15} />}
                    iconRight={
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="p-0.5 rounded">
                        {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    }
                    error={errors.password}
                  />
                  <Button fullWidth loading={loading} onClick={handlePasswordLogin} icon={!loading && <ArrowRight size={16} />}>
                    Sign In
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-8">
            New to Cuore?{" "}
            <Link to="/onboarding" className="text-brand-600 hover:text-brand-700 font-medium">
              Create account
            </Link>
          </p>
          <p className="text-center text-xs text-slate-500 dark:text-slate-400 mt-3">
            Clinic chain or corporate admin?{" "}
            <Link to="/org/login" className="text-brand-600 hover:text-brand-700 font-medium">
              Sign in here
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
